import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import SEOHelmet from '../components/SEOHelmet';

const api = () => axios.create({
  baseURL: '/api',
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
});

const PLANS = [
  {
    id: 'starter',
    name: 'Starter',
    price: 0,
    period: 'forever',
    commission: 18,
    maxShifts: 3,
    description: 'Try it risk-free',
    badge: null,
    highlight: false,
    features: [
      '3 active shifts at a time',
      '18% commission on completed shifts',
      'Basic applicant management',
      'In-app worker messaging',
      'GPS clock-in verification',
      'Standard support',
    ],
    cta: 'Get Started',
    savingNote: null,
  },
  {
    id: 'growth',
    name: 'Growth',
    price: 99,
    period: '/month',
    commission: 10,
    maxShifts: null,
    description: 'For regular hirers',
    badge: 'MOST POPULAR',
    highlight: true,
    features: [
      'Unlimited active shifts',
      '10% commission — save 44% vs Starter',
      'Full analytics dashboard',
      'Featured shift placement',
      'Live map — see workers in real time',
      'Welfare check-ins',
      'Client portal (3 portals)',
      'Shift Proof PDFs',
      'Priority email support',
    ],
    cta: 'Upgrade to Growth',
    savingNote: 'Saves money at 12+ shifts/month',
  },
  {
    id: 'pro',
    name: 'Pro',
    price: 249,
    period: '/month',
    commission: 6,
    maxShifts: null,
    description: 'For high-volume operations',
    badge: null,
    highlight: false,
    features: [
      'Unlimited active shifts',
      '6% commission — save 67% vs Starter',
      'Bulk shift posting (10+ at once)',
      'Unlimited client portals',
      'Priority worker placement',
      'Licence requirement filters',
      'Custom compliance PDF reports',
      'Payroll timesheet export',
      'Priority support (4hr response)',
    ],
    cta: 'Upgrade to Pro',
    savingNote: 'Saves money at 37+ shifts/month',
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    price: 599,
    period: '/month',
    commission: 3,
    maxShifts: null,
    description: 'For large operations & chains',
    badge: null,
    highlight: false,
    features: [
      'Unlimited active shifts',
      '3% commission — lowest in market',
      'Dedicated account manager',
      'SLA: shift filled in <2hrs or fee waived',
      'API access for bulk posting',
      'White-label client portal',
      'Xero / MYOB payroll export',
      'Multi-site dashboard',
      'Invoice billing available',
    ],
    cta: 'Contact Us',
    savingNote: 'Saves money at 84+ shifts/month',
  },
];

export default function PricingPage() {
  const navigate = useNavigate();
  const http = api();
  const [upgrading, setUpgrading] = useState(null);
  const [toast, setToast] = useState('');
  // const user = JSON.parse(localStorage.getItem('user') || '{}');

  const handleUpgrade = async (planId) => {
    if (planId === 'enterprise') {
      window.location.href = 'mailto:sales@shiftmatch.work?subject=Enterprise%20Plan%20Inquiry';
      return;
    }
    setUpgrading(planId);
    try {
      const res = await http.post('/subscription', { plan: planId });
      setToast(res.data.message || 'Upgraded!');
    } catch (err) {
      setToast(err.response?.data?.error || 'Failed to upgrade');
    }
    setUpgrading(null);
  };

  return (
    <div className="min-h-screen bg-neutral-50">
      <SEOHelmet title="Pricing" description="ShiftMatch employer plans. Free forever, or upgrade for premium features." />

      <div className="bg-white border-b border-neutral-200 sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="p-2 hover:bg-neutral-100 rounded-lg text-neutral-500">←</button>
          <h1 className="font-bold text-neutral-900">Employer Plans</h1>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-8">
        <div className="text-center mb-8">
          <h2 className="text-2xl font-black text-neutral-900 mb-2">Hire faster. Pay less as you grow.</h2>
          <p className="text-neutral-500 text-sm max-w-lg mx-auto mb-3">
            Commission only on completed shifts — you never pay unless the job gets done.
            High-volume employers save thousands per month on lower rates.
          </p>
          <div className="inline-flex items-center gap-2 bg-brand-50 border border-brand-100 rounded-full px-4 py-1.5 text-xs text-brand-700 font-medium">
            <span>💡</span>
            <span>Traditional labour hire agencies charge 20–35%. We start at 18% and go as low as 3%.</span>
          </div>
        </div>

        {toast && (
          <div className="max-w-md mx-auto mb-6 p-3 bg-brand-50 border border-brand-200 rounded-xl text-sm text-brand-700 font-medium text-center">
            {toast}
          </div>
        )}

        {/* Comparison bar */}
        <div className="max-w-2xl mx-auto mb-6 bg-white border border-neutral-100 rounded-2xl p-4 shadow-sm">
          <p className="text-xs font-semibold text-neutral-500 mb-3 text-center">💰 Commission rate by plan</p>
          <div className="flex items-end gap-2 h-14">
            {PLANS.map((plan, i) => {
              const heights = ['100%', '55%', '33%', '17%'];
              const colors = ['bg-neutral-300', 'bg-brand-400', 'bg-brand-500', 'bg-brand-700'];
              return (
                <div key={plan.id} className="flex-1 flex flex-col items-center gap-1">
                  <span className="text-[10px] font-bold text-neutral-700">{plan.commission}%</span>
                  <div className={`w-full rounded-t-md ${colors[i]}`} style={{ height: heights[i] }} />
                  <span className="text-[9px] text-neutral-400">{plan.name}</span>
                </div>
              );
            })}
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {PLANS.map(plan => (
            <div key={plan.id} className={`bg-white rounded-2xl border shadow-sm p-5 flex flex-col relative ${
              plan.highlight ? 'border-brand-400 ring-2 ring-brand-100' : 'border-neutral-100'
            }`}>
              {plan.badge && (
                <span className="absolute -top-2.5 left-1/2 -translate-x-1/2 text-[10px] px-3 py-0.5 bg-brand-500 text-white font-bold rounded-full">
                  {plan.badge}
                </span>
              )}
              <h3 className="font-bold text-neutral-900 text-lg">{plan.name}</h3>
              <p className="text-xs text-neutral-400 mb-1">{plan.description}</p>
              <div className="mb-1">
                <span className="text-3xl font-black text-neutral-900">{plan.price === 0 ? '$0' : `$${plan.price}`}</span>
                <span className="text-sm text-neutral-400">{plan.period}</span>
              </div>
              <div className="mb-3">
                <span className="inline-block text-xs font-bold px-2 py-0.5 rounded-full bg-brand-50 text-brand-700">
                  {plan.commission}% per shift
                </span>
              </div>
              {plan.savingNote && (
                <p className="text-[10px] text-brand-600 font-medium mb-2 italic">{plan.savingNote}</p>
              )}
              <ul className="space-y-2 mb-5 flex-1">
                {plan.features.map(f => (
                  <li key={f} className="text-xs text-neutral-600 flex items-start gap-2">
                    <span className="text-brand-500 mt-0.5">✓</span>
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              <button
                onClick={() => plan.id !== 'starter' && handleUpgrade(plan.id)}
                disabled={plan.id === 'starter' || upgrading === plan.id}
                className={`w-full py-2.5 rounded-xl font-bold text-sm transition ${
                  plan.id === 'starter'
                    ? 'bg-neutral-100 text-neutral-400 cursor-default'
                    : plan.highlight
                    ? 'bg-brand-500 hover:bg-brand-600 text-white shadow-sm'
                    : 'border border-neutral-200 text-neutral-700 hover:bg-brand-50 hover:text-brand-700 hover:border-brand-200'
                }`}>
                {upgrading === plan.id ? 'Processing...' : plan.cta}
              </button>
            </div>
          ))}
        </div>

        {/* Break-even calculator */}
        <div className="mt-8 max-w-2xl mx-auto bg-white border border-neutral-100 rounded-2xl p-5 shadow-sm">
          <h4 className="font-bold text-neutral-800 text-sm mb-1">📊 When does upgrading save money?</h4>
          <p className="text-xs text-neutral-400 mb-4">Based on average shift payout of $135 (5hrs × $27/hr)</p>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-neutral-100">
                  <th className="text-left py-2 text-neutral-500 font-medium">Shifts/month</th>
                  <th className="text-right py-2 text-neutral-500 font-medium">Starter (18%)</th>
                  <th className="text-right py-2 text-neutral-500 font-medium">Growth (10%)</th>
                  <th className="text-right py-2 text-neutral-500 font-medium">Pro (6%)</th>
                  <th className="text-right py-2 text-neutral-500 font-medium">Enterprise (3%)</th>
                </tr>
              </thead>
              <tbody>
                {[5, 15, 30, 60, 100].map(shifts => {
                  const avg = 135;
                  const s = Math.round(shifts * avg * 0.18);
                  const g = Math.round(99 + shifts * avg * 0.10);
                  const p = Math.round(249 + shifts * avg * 0.06);
                  const e = Math.round(599 + shifts * avg * 0.03);
                  const best = Math.min(s, g, p, e);
                  return (
                    <tr key={shifts} className="border-b border-neutral-50">
                      <td className="py-2 font-semibold text-neutral-700">{shifts}</td>
                      <td className={`py-2 text-right font-mono ${s === best ? 'text-brand-600 font-bold' : 'text-neutral-500'}`}>${s.toLocaleString()}</td>
                      <td className={`py-2 text-right font-mono ${g === best ? 'text-brand-600 font-bold' : 'text-neutral-500'}`}>${g.toLocaleString()}</td>
                      <td className={`py-2 text-right font-mono ${p === best ? 'text-brand-600 font-bold' : 'text-neutral-500'}`}>${p.toLocaleString()}</td>
                      <td className={`py-2 text-right font-mono ${e === best ? 'text-brand-600 font-bold' : 'text-neutral-500'}`}>${e.toLocaleString()}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <p className="text-[10px] text-neutral-400 mt-3">Bold = cheapest option at that volume. Figures include monthly plan fee + commission.</p>
        </div>

        <div className="mt-6 text-center">
          <p className="text-xs text-neutral-400 max-w-md mx-auto">
            All plans: free worker registration, in-app chat, GPS clock-in, identity verification,
            welfare checks, safety toolkit, review system. No lock-in contracts. Cancel anytime.
          </p>
        </div>
      </div>
    </div>
  );
}
